import { kybStyles } from "../kybStyles";
import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

interface KybDialogProps {
  title: string;
  description?: string;
  busy?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const focusableSelector =
  'button:not(:disabled), a[href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/** Keeps modal focus behavior while allowing the app's snackbars above the overlay. */
const KybDialog: React.FC<KybDialogProps> = ({
  title,
  description,
  busy = false,
  onClose,
  children,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef(document.activeElement as HTMLElement | null);
  const busyRef = useRef(busy);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    busyRef.current = busy;
    onCloseRef.current = onClose;
  }, [busy, onClose]);

  useEffect(() => {
    const returnFocus = returnFocusRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!busyRef.current) onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const controls = Array.from(
        dialog.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter((element) => !element.closest('[hidden], [aria-hidden="true"]'));
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) {
        event.preventDefault();
        dialog.focus();
      } else if (
        event.shiftKey &&
        (document.activeElement === first ||
          !dialog.contains(document.activeElement))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last ||
          !dialog.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      if (returnFocus?.isConnected) returnFocus.focus();
    };
  }, []);

  useEffect(() => {
    dialogRef.current
      ?.querySelector<HTMLElement>(
        "[data-kyb-initial-focus], input:not(:disabled)",
      )
      ?.focus();
  }, [title]);

  return createPortal(
    <div
      className="light fixed inset-0 z-[1000] flex items-center justify-center bg-[rgb(21_24_44_/_28%)]"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={`w-[min(440px,calc(100vw-32px))] max-h-[calc(100dvh-48px)] m-auto p-0 overflow-auto border border-border-subtle rounded-[12px] text-text-base bg-bg-page-body shadow-[0_16px_64px_rgb(21_24_44_/_16%)] [font-family:Inter,sans-serif] ${kybStyles.motion}`}
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        aria-busy={busy}
      >
        <div className="p-6">
          <div className="flex items-center justify-between gap-4 mb-2">
            <h2
              id={titleId}
              className="m-0 text-[18px] font-semibold leading-[1.4] tracking-[-0.3px]"
            >
              {title}
            </h2>
            <button
              type="button"
              className={kybStyles.iconButton}
              aria-label="Close dialog"
              disabled={busy}
              onClick={onClose}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                close
              </span>
            </button>
          </div>
          {description && (
            <p
              id={descriptionId}
              className="mt-0 mb-6 text-text-subtle text-[12px] leading-[1.7] [overflow-wrap:anywhere]"
            >
              {description}
            </p>
          )}
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default KybDialog;
